import { Injectable } from '@angular/core';
import { WebsocketInit } from '@app/core/models/connection-status';
import { ApiService } from '@app/core/services/api.service';
import { WebSocketService } from '@app/core/services/websocket.service';
import { NOTIFICATION_TYPE, SMSMessage, STREAM_UPDATE_TYPE, UnreadCount } from '@app/sms/models/sms.models';
import { environment } from '@environment/environment';
import moment from 'moment';
import { BehaviorSubject, catchError, filter, from, map, mergeMap, of, switchMap, toArray } from 'rxjs';

import { SMSService } from './sms.service';
import { SMSConversationStorageService } from './sms-conversation-storage.service';

/**
 * Handles the logic managing "unread" and "read" messages which
 * includes:
 * - Marking messages as "read"
 *
 * @export
 * @class SMSUnreadMessageService
 */
@Injectable({
  providedIn: 'root',
})
export class SMSUnreadMessageService extends ApiService implements WebsocketInit {
  private readonly unreadCountSubject = new BehaviorSubject<UnreadCount>({});
  public readonly unreadCount$ = this.unreadCountSubject.asObservable();

  private readonly unreadPhoneNumberCountSubject: BehaviorSubject<UnreadCount> = new BehaviorSubject<UnreadCount>({});
  public readonly unreadPhoneNumberCount$ = this.unreadPhoneNumberCountSubject.asObservable();

  constructor(
    private webSocketService: WebSocketService,
    private smsService: SMSService,
    private smsConversationStorageService: SMSConversationStorageService
  ) {
    super(environment.messageHubGateway);

    // Build initial unread counts from in-memory cache.
    // Will be empty if called before seed completes — the bulkDataChanged$ subscription below
    // will rebuild once seed data arrives.
    this.unreadCountSubject.next(this.smsConversationStorageService.buildUnreadCounts());

    // Rebuild unread counts when in-memory cache changes (seed, API page merge, etc.)
    this.smsConversationStorageService.bulkDataChanged$.subscribe(() => {
      this.unreadCountSubject.next(this.smsConversationStorageService.buildUnreadCounts());
    });

    // Update when a new conversation is added
    this.smsService.conversationAdded$.subscribe((conversation) => {
      if (conversation.unreadMessageCount > 0) {
        const current = this.unreadCountSubject.value;
        this.unreadCountSubject.next({
          ...current,
          [conversation.conversationId]: conversation.unreadMessageCount,
        });
      }
    });

    // Update when a conversation's unread count changes
    this.smsService.conversationUpdated$.subscribe((conversation) => {
      const current = this.unreadCountSubject.value;
      const currentCount = current[conversation.conversationId] || 0;

      // Only update if the count changed
      if (currentCount !== conversation.unreadMessageCount) {
        this.unreadCountSubject.next({
          ...current,
          [conversation.conversationId]: conversation.unreadMessageCount,
        });
      }
    });

    // Any time the unread count changes, also recalculate the unread count for our phone numbers
    this.unreadCount$
      .pipe(
        map((unreadCount) => ({ unreadCount, conversationIds: Object.keys(unreadCount) })),
        switchMap(({ unreadCount, conversationIds }) =>
          from(conversationIds).pipe(
            mergeMap((id) => this.smsService.getCachedConversationWithId(id)),
            filter((result): result is NonNullable<typeof result> => result !== null), // Type guard to filter out null results
            toArray(), // Collect all non-null results into a single array
            map((results) => ({ unreadCount, conversations: results })) // Combine unreadCount and results
          )
        )
      )
      .subscribe(({ unreadCount, conversations }) => {
        const phoneCount: UnreadCount = {};
        for (const conversation of conversations) {
          phoneCount[conversation.local] =
            (phoneCount[conversation.local] || 0) + (unreadCount[conversation.conversationId] || 0);
        }
        this.unreadPhoneNumberCountSubject.next({ ...this.unreadPhoneNumberCountSubject.value, ...phoneCount });
      });
  }

  public bindSocketEvents(): void {
    this.webSocketService.on(
      'SMSConversationReadMarkerUpdated',
      (data: { conversationId: string; readTimestamp: string }) => {
        this.smsService.getCachedConversationWithId(data.conversationId).then((conv) => {
          const isUnread = !!conv && !!data.readTimestamp && data.readTimestamp < conv.lastMessageTimestamp;
          this.setUnreadCount(data.conversationId, isUnread ? 1 : 0);
        });
      }
    );

    this.webSocketService.on('SMSMessageReceived', (message: SMSMessage) => {
      // Filter out any notification messages that aren't streamupdate type. Standard messages won't have the `notification`
      // prop set and when it is set, only stream updates should be processed.
      if (
        message.type === NOTIFICATION_TYPE ||
        (message.notification?.type && message.notification.type !== STREAM_UPDATE_TYPE)
      ) {
        return;
      }

      // When a message is received on the socket, check it see if we're currently rendering that conversation.
      // If not, increment the unread count.
      // TODO: We may want to eventually make this more sophisticated so if the conversationId _does_ match the current
      // conversation, we still may increment the count if the user isn't scrolled to the bottom of the conversation.
      if (message.conversationId && document.location.href.search(message.conversationId) === -1) {
        this.incrementUnreadCount(message.conversationId);
      }
    });

    this.webSocketService.on('SMSMessageSent', (data: { conversationId: string }) => {
      this.updateReadMarker(data.conversationId);
    });
  }

  public getUnreadCount(conversationId: string): number {
    return this.unreadCountSubject.getValue()[conversationId] || 0;
  }

  private incrementUnreadCount(conversationId: string): void {
    const count = this.getUnreadCount(conversationId) + 1;
    this.setUnreadCount(conversationId, count);
  }

  private setUnreadCount(conversationId: string, count: number): void {
    const unreadCount = this.unreadCountSubject.getValue();
    unreadCount[conversationId] = count;

    // Update the value on the conversation in storage
    this.smsService.updateConversationById(conversationId, { unreadMessageCount: count });
    this.unreadCountSubject.next({ ...unreadCount });
  }

  public updateReadMarker(conversationId: string, readTimestamp?: string) {
    const body = readTimestamp ? { readTimestamp } : {};

    // Passing `null` for body will update the read marker on the server to the latest message's timestamp
    return this.put(`sms/conversations/${conversationId}/read-marker`, body)
      .pipe(
        catchError(() => {
          return of();
        })
      )
      .subscribe(() => {
        this.setUnreadCount(conversationId, readTimestamp ? 1 : 0);
      });
  }

  public updateAllConversationsReadMarker() {
    return this.put(`sms/messages/read-marker`, {}).subscribe();
  }

  public async markConversationUnread(conversationId: string) {
    const conversation = await this.smsService.getCachedConversationWithId(conversationId);

    if (conversation) {
      const timestamp = moment.utc(conversation.lastMessageTimestamp).add(-1, 'second').toISOString();
      this.updateReadMarker(conversationId, timestamp);
    }
  }
}
